import { useQuery } from '@apollo/client';
import { useQuery as useDfcQuery } from 'data-fetching-client';
import { GET_OBJECT_TYPE_DEFINITION } from '../api/objectTypeDefinition';
import { useMemo } from 'react';
import { useFetchOrderedActivityButtons } from 'crm-activity-creator-widgets/hooks/useFetchOrderedActivityButtons';
import { SUBJECT_PERMISSIONS_QUERY } from '../api/subjectPermissions';
import { GET_HIGHLIGHT_SUBJECT } from '../api/subject';
import { GET_PROPERTY_DEFINITIONS } from '../api/propertyDefinitions';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import Raven from 'raven-js';
const formatPropertyDefinitions = propertyDefinitions => {
  const result = propertyDefinitions.reduce((acc, propertyDefinition) => {
    acc.set(propertyDefinition.property.name, propertyDefinition);
    return acc;
  }, new Map());
  return result;
};
export const useAsyncData = (objectTypeId, objectId) => {
  const {
    data: subjectPermissionsData,
    loading: subjectPermissionsLoading,
    error: subjectPermissionsError
  } = useQuery(SUBJECT_PERMISSIONS_QUERY, {
    partialRefetch: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      objectType: objectTypeId,
      subjectId: objectId
    }
  });
  const {
    data: subjectData,
    loading: subjectLoading,
    error: subjectError,
    refetch: refetchSubject
  } = useDfcQuery(GET_HIGHLIGHT_SUBJECT, {
    variables: {
      objectTypeId,
      objectId
    }
  });
  const {
    data: objectTypeDefinitionData,
    loading: objectTypeDefinitionLoading,
    error: objectTypeDefinitionError
  } = useDfcQuery(GET_OBJECT_TYPE_DEFINITION, {
    variables: {
      objectTypeId
    }
  });
  const {
    data: propertyDefinitionsData,
    loading: propertyDefinitionsLoading,
    error: propertyDefinitionsError
  } = useDfcQuery(GET_PROPERTY_DEFINITIONS, {
    variables: {
      objectTypeId
    }
  });
  const orderedActivityButtonsResult = useFetchOrderedActivityButtons({
    objectTypeId,
    subjectId: String(objectId) || ''
  });
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECTS, message => {
    message.forEach(object => {
      if (object.objectTypeId === objectTypeId && parseInt(object.objectId, 10) === objectId) {
        refetchSubject().catch(error => {
          Raven.captureException(error, {
            extra: {
              objectTypeId,
              objectId
            }
          });
        });
      }
    });
  });
  return useMemo(() => {
    var _subjectData$highligh;
    return {
      subjectPermissionsResult: {
        data: subjectPermissionsData,
        loading: subjectPermissionsLoading,
        error: subjectPermissionsError
      },
      subjectResult: {
        data: (_subjectData$highligh = subjectData === null || subjectData === void 0 ? void 0 : subjectData.highlightSubject) !== null && _subjectData$highligh !== void 0 ? _subjectData$highligh : undefined,
        loading: subjectLoading,
        error: subjectError,
        refetch: refetchSubject
      },
      objectTypeDefinitionResult: {
        data: objectTypeDefinitionData,
        loading: objectTypeDefinitionLoading,
        error: objectTypeDefinitionError
      },
      propertyDefinitionsResult: {
        data: propertyDefinitionsData !== null && propertyDefinitionsData !== void 0 && propertyDefinitionsData.propertyDefinitions ? formatPropertyDefinitions(propertyDefinitionsData === null || propertyDefinitionsData === void 0 ? void 0 : propertyDefinitionsData.propertyDefinitions) : undefined,
        loading: propertyDefinitionsLoading,
        error: propertyDefinitionsError
      },
      orderedActivityButtonsResult
    };
  }, [subjectData, subjectLoading, subjectError, refetchSubject, subjectPermissionsData, subjectPermissionsLoading, subjectPermissionsError, objectTypeDefinitionData, objectTypeDefinitionLoading, objectTypeDefinitionError, propertyDefinitionsData, propertyDefinitionsLoading, propertyDefinitionsError, orderedActivityButtonsResult]);
};